import { Participant } from '../models/dashboardTypes';
import { SessionStatus, calculateSessionStatus, getSessionWindow } from '../pages/Dashboard/Dashboard';
import { formatDate } from './dateUtils';

const MAX_SESSIONS = 13;

// Helper function for session completion check
const isSessionCompleted = (session: unknown): boolean => {
  return !!(session && typeof session === 'object' && 'sessionEndDateTime' in session && session.sessionEndDateTime);
};

// For an offboarded participant, decide which session column the "Offboarded" / "Rejoin until"
// badge belongs in. Rule: the later (further-right) of
//   (a) the slot immediately after the last completed session, and
//   (b) the session whose date window contains the offboarding date.
// This keeps the badge in a date-appropriate position instead of the first incomplete slot,
// and never drops it in between completed sessions.
const getOffboardTargetSession = (item: Participant): number => {
  let lastCompleted = 0;
  for (const session of item.sessions ?? []) {
    if (isSessionCompleted(session)) {
      const n = parseInt(session.sessionNumber.replace('session', ''));
      if (n > lastCompleted) {
        lastCompleted = n;
      }
    }
  }

  let offboardWindowIndex = 1;
  if (item.offboardedOn && item.activatedOn != null) {
    const offboardDate = new Date((item.offboardedOn as number) * 1000);
    for (let n = 1; n <= MAX_SESSIONS; n++) {
      if (getSessionWindow(item.activatedOn as number, n).openDate <= offboardDate) {
        offboardWindowIndex = n;
      }
    }
  }

  return Math.min(MAX_SESSIONS, Math.max(lastCompleted + 1, offboardWindowIndex));
};

export const processParticipantSessions = (item: Participant): { [key: string]: SessionStatus } => {
  const sessionEndDates: { [key: string]: SessionStatus } = {};
  const isOffboarded = item.registrationState === 'OFFBOARD';
  let foundFirstDue = false;
  const offboardTargetSession = isOffboarded ? getOffboardTargetSession(item) : -1;

  for (let sessionNumber = 1; sessionNumber <= MAX_SESSIONS; sessionNumber++) {
    const session = item.sessions?.find((s) => parseInt(s.sessionNumber.replace('session', '')) === sessionNumber);

    if (session && isSessionCompleted(session)) {
      // For active participants, don't show completed sessions after first due
      if (!isOffboarded && foundFirstDue) {
        sessionEndDates[sessionNumber] = { status: '' };
      } else {
        const typedSession = session as { sessionEndDateTime: string };
        sessionEndDates[sessionNumber] = {
          status: 'Completed',
          subtitle: formatDate(new Date(typedSession.sessionEndDateTime)),
        };
      }
    } else {
      if (isOffboarded) {
        // Offboard badge on the date-appropriate slot; earlier incomplete slots are "Missed"
        // (the participant can never complete them); later slots (after offboarding) stay blank.
        if (sessionNumber === offboardTargetSession) {
          sessionEndDates[sessionNumber] = calculateSessionStatus(item, sessionNumber);
        } else if (sessionNumber < offboardTargetSession) {
          sessionEndDates[sessionNumber] = {
            status: 'Missed',
            subtitle:
              item.activatedOn != null
                ? formatDate(getSessionWindow(item.activatedOn as number, sessionNumber).dueDate)
                : null,
          };
        } else {
          sessionEndDates[sessionNumber] = { status: '' };
        }
      } else {
        const sessionStatus = calculateSessionStatus(item, sessionNumber);
        if (sessionStatus.status === 'Missed' || (sessionStatus.status === 'Due' && !foundFirstDue)) {
          sessionEndDates[sessionNumber] = sessionStatus;
          if (sessionStatus.status === 'Due') {
            foundFirstDue = true;
          }
        } else {
          sessionEndDates[sessionNumber] = { status: '' };
        }
      }
    }
  }

  return sessionEndDates;
};

export { isSessionCompleted };
